// src/components/UserForm.jsx
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { userSchema } from "../../schemas/userSchema";
import {
  User,
  Mail,
  Phone,
  CalendarClock,
  Key,
  Eye,
  EyeOff,
} from "lucide-react";
import Select from "react-select";
import LoadingButton from "../../components/LoadingButton";

export default function UserForm({
  mode,
  defaultValues,
  onSubmit,
  onCancel,
  addressList,
  path = "/users",
  submissionLoading,
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    control,
  } = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: { ...defaultValues, mode },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Full Name */}
      <div>
        <label className="block text-sm font-medium mb-1">
          Full Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            {...register("full_name")}
            readOnly={mode === "view"}
            className="pl-10 pr-3 py-2 border rounded w-full"
          />
        </div>
        {errors.full_name && (
          <p className="text-red-500 text-xs">{errors.full_name.message}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium mb-1">
          Email <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            {...register("email")}
            readOnly={mode === "view"}
            className="pl-10 pr-3 py-2 border rounded w-full"
          />
        </div>
        {errors.email && (
          <p className="text-red-500 text-xs">{errors.email.message}</p>
        )}
      </div>

      {/* Phone & Join Date */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Country Code Select */}
          <div className="relative flex-grow">
            <label className="block text-sm font-medium mb-1">
              Country Code
            </label>
            <div className="relative flex-grow">
              <Controller
                name="country_code"
                control={control}
                render={({ field: { value, onChange, onBlur, ref } }) => {
                  const options = addressList.map((item) => ({
                    label: `${item.flag} ${item.phonecode}`,
                    value: item.phonecode,
                  }));
                  const selectedOption =
                    options.find((opt) => opt.value === value) || null;
                  return (
                    <Select
                      value={selectedOption}
                      onChange={(opt) => onChange(opt?.value)}
                      onBlur={onBlur}
                      ref={ref}
                      options={options}
                      isDisabled={mode === "view"}
                      // className="w-40"
                      isClearable
                    />
                  );
                }}
              />
              {errors.country_code && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.country_code.message}
                </p>
              )}
            </div>
          </div>

          {/* Phone Number Input */}
          <div className="relative flex-grow">
            <label className="block text-sm font-medium mb-1">Phone</label>
            <div className="relative flex-grow">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                {...register("phone")}
                readOnly={mode === "view"}
                className="pl-10 pr-3 py-2 border rounded w-full"
                placeholder="123 456 7890"
              />
            </div>
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">
                {errors.phone.message}
              </p>
            )}
          </div>
        </div>

        {/* Error messages */}
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Join Date</label>
        <div className="relative">
          <CalendarClock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="date"
            {...register("created_at")}
            readOnly={mode === "view"}
            className="pl-10 pr-3 py-2 border rounded w-full"
          />
        </div>
        {errors.joinDate && (
          <p className="text-red-500 text-xs">{errors.joinDate.message}</p>
        )}
      </div>

      {/* Subscription & Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Subscription</label>
          <select
            {...register("subscriptionStatus")}
            disabled={mode === "view"}
            className="px-3 py-2 border rounded w-full"
          >
            <option value="Free">Free</option>
            <option value="Premium">Premium</option>
          </select>
          {errors.subscriptionStatus && (
            <p className="text-red-500 text-xs">
              {errors.subscriptionStatus.message}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Account Status
          </label>
          <select
            {...register("is_active")}
            disabled={mode === "view"}
            className="px-3 py-2 border rounded w-full"
          >
            <option value={true}>Active</option>
            <option value={false}>Inactive</option>
          </select>
          {errors.status && (
            <p className="text-red-500 text-xs">{errors.status.message}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">User Type</label>
        <select
          {...register("user_type")}
          disabled={mode === "view" || mode === "edit"}
          className="px-3 py-2 border rounded w-full"
          defaultValue={path === "/admin-users" ? "sub_admin" : "user"}
        >
          <option value={"admin"} disabled>
            Super Admin
          </option>
          <option value={"sub_admin"}>Sub Admin</option>
          <option value={"user"}>User</option>
        </select>
        {errors.status && (
          <p className="text-red-500 text-xs">{errors.status.message}</p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded text-gray-700"
        >
          {mode === "view" ? "Close" : "Cancel"}
        </button>
        {mode !== "view" && (
          <button
            type="submit"
            disabled={submissionLoading}
            className="px-4 py-2 bg-primary text-white rounded flex items-center justify-center gap-2"
          >
            {submissionLoading ? (
              <LoadingButton />
            ) : (
              <>{mode === "edit" ? "Save Changes" : "Create User"}</>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
